import { useState, useRef, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Monogram from "src/components/Monogram";
import Input from "src/components/Input";
import {
  ChevronRightIcon,
  LocationPinTwoToneIcon,
  CloseIcon,
  LocationChangeTwoToneIcon,
  SearchIcon,
} from "src/assets/icons";
import {
  Container,
  Header,
  HeaderLeft,
  HeaderTitle,
  HeaderIcon,
  HeaderCloseButton,
  HeaderCloseButtonLabel,
  Content,
  Title,
  LocationEmoji,
  SearchWrapper,
  SearchIconWrapper,
  LocationListWrapper,
  LocationList,
  FadeOverlay,
  LocationCard,
  LocationIcon,
  LocationInfo,
  LocationName,
  LocationAddress,
  ChevronIcon,
  ContentWrapper,
  BottomFadeOverlay,
} from "./LocationSwitcher.styles";
import type { DealerUserInfoType } from "src/context/DealerUserInfoContext";

const constructAddress = (dealer: DealerUserInfoType): string => {
  const parts: string[] = [];

  if (dealer.dealer_street1) {
    parts.push(dealer.dealer_street1);
  }

  if (dealer.dealer_street2 && dealer.dealer_street2.trim()) {
    parts.push(dealer.dealer_street2);
  }

  const cityStateZip: string[] = [];
  if (dealer.dealer_city) {
    cityStateZip.push(dealer.dealer_city);
  }

  if (dealer.dealer_state) {
    cityStateZip.push(dealer.dealer_state);
  }

  if (dealer.dealer_zip_code) {
    cityStateZip.push(dealer.dealer_zip_code);
  }

  if (cityStateZip.length > 0) {
    parts.push(cityStateZip.join(", "));
  }

  return parts.length > 0 ? parts.join(", ") : "";
};

type LocationSwitcherProps = {
  dealerOptions: DealerUserInfoType[];
  onLocationSelect?: (dealerId: string) => void;
  selectedDealerId?: string;
  showCloseButton?: boolean;
  onClose?: () => void;
};

export const LocationSwitcher = ({
  dealerOptions,
  onLocationSelect,
  selectedDealerId,
  showCloseButton = false,
  onClose,
}: LocationSwitcherProps) => {
  const navigate = useNavigate();
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [failedLogos, setFailedLogos] = useState<Set<string>>(new Set());
  const [showTopFade, setShowTopFade] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const listRef = useRef<HTMLDivElement>(null);

  const filteredDealers = useMemo(() => {
    if (!searchTerm.trim()) {
      return dealerOptions;
    }

    const searchTerms = searchTerm
      .toLowerCase()
      .split(/\s+/)
      .filter((term) => term.length > 0);

    return dealerOptions.filter((dealer) => {
      const dealerName = dealer.dealer_name?.toLowerCase() || "";
      const address = constructAddress(dealer).toLowerCase() || "";

      return searchTerms.some(
        (term) => dealerName.includes(term) || address.includes(term),
      );
    });
  }, [dealerOptions, searchTerm]);

  useEffect(() => {
    const checkScroll = () => {
      const element = listRef.current;
      if (!element) return;

      const isScrollable = element.scrollHeight > element.clientHeight;
      const scrollTop = element.scrollTop;
      const isAtTop = scrollTop < 10;
      const isAtBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight < 10;

      setShowTopFade(isScrollable && !isAtTop);
      setShowBottomFade(isScrollable && !isAtBottom);
    };

    const element = listRef.current;
    if (element) {
      checkScroll();
      element.addEventListener("scroll", checkScroll);
      window.addEventListener("resize", checkScroll);

      return () => {
        element.removeEventListener("scroll", checkScroll);
        window.removeEventListener("resize", checkScroll);
      };
    }
  }, [filteredDealers]);

  const handleLocationClick = (dealer: DealerUserInfoType) => {
    if (onLocationSelect) {
      onLocationSelect(dealer.dealer_id ?? "");
    } else {
      if (dealer.dealer_id === "porchpass") {
        void navigate("/porchpass/dealers");
      } else {
        void navigate(`/${dealer.dealer_id ?? ""}/dashboard`, {
          replace: true,
        });
      }
    }
  };

  const handleImageError = (dealerId: string | undefined) => {
    if (!dealerId) return;
    setFailedLogos((prev) => new Set(prev).add(dealerId));
  };

  useEffect(() => {
    setFailedLogos(new Set());
  }, [dealerOptions]);

  if (!dealerOptions || dealerOptions.length === 0) {
    return null;
  }

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <HeaderIcon>
            <LocationChangeTwoToneIcon />
          </HeaderIcon>
          <HeaderTitle>Switch location</HeaderTitle>
        </HeaderLeft>
        {showCloseButton && onClose && (
          <HeaderCloseButton onClick={onClose} type="button">
            <HeaderCloseButtonLabel>Close</HeaderCloseButtonLabel>
            <CloseIcon width="14" height="14" />
          </HeaderCloseButton>
        )}
      </Header>
      <ContentWrapper ref={listRef}>
        <FadeOverlay isVisible={showTopFade} position="top" />
        <Content>
          <Title>
            What location{" "}
            <LocationEmoji>
              <LocationPinTwoToneIcon />
            </LocationEmoji>{" "}
            would you like to use?
          </Title>
          {dealerOptions.length > 10 && (
            <SearchWrapper>
              <SearchIconWrapper>
                <SearchIcon />
              </SearchIconWrapper>
              <Input
                value={searchTerm}
                showError={false}
                placeholder="Search locations..."
                onChange={(e) => setSearchTerm(e.target.value)}
                onFocus={() => {}}
                onBlur={() => {}}
                ariaLabel="Search locations"
                size="s"
              />
            </SearchWrapper>
          )}
          <LocationListWrapper>
            <LocationList>
              {filteredDealers.length === 0 ? (
                <LocationAddress
                  style={{ textAlign: "center", padding: "24px" }}
                >
                  No locations found
                </LocationAddress>
              ) : (
                filteredDealers.map((dealer, index) => {
                  const isHovered = hoveredIndex === index;
                  const isSelected = selectedDealerId === dealer.dealer_id;
                  const address = constructAddress(dealer);
                  const logoUrl = dealer.dealer_logo_url;
                  const logoFailed = dealer.dealer_id
                    ? failedLogos.has(dealer.dealer_id)
                    : false;
                  const hasLogo = !!logoUrl && !logoFailed;

                  const isFocused = focusedIndex === index;
                  const isActive = activeIndex === index;

                  return (
                    <LocationCard
                      key={dealer.dealer_id}
                      onClick={() => handleLocationClick(dealer)}
                      onMouseEnter={() => setHoveredIndex(index)}
                      onMouseLeave={() => {
                        setHoveredIndex(null);
                        setActiveIndex(null);
                      }}
                      onFocus={() => setFocusedIndex(index)}
                      onBlur={() => setFocusedIndex(null)}
                      onMouseDown={() => setActiveIndex(index)}
                      onMouseUp={() => setActiveIndex(null)}
                      isHovered={isHovered}
                      isSelected={isSelected}
                      tabIndex={0}
                    >
                      <LocationIcon hasLogo={hasLogo}>
                        {hasLogo ? (
                          <img
                            src={logoUrl}
                            alt={dealer.dealer_name}
                            onError={() => handleImageError(dealer.dealer_id)}
                          />
                        ) : (
                          <Monogram size={34}>
                            {dealer.dealer_name?.charAt(0) || "L"}
                          </Monogram>
                        )}
                      </LocationIcon>
                      <LocationInfo>
                        <LocationName isSelected={isSelected}>
                          {dealer.dealer_name}
                        </LocationName>
                        {address && (
                          <LocationAddress isSelected={isSelected}>
                            {address}
                          </LocationAddress>
                        )}
                      </LocationInfo>
                      <ChevronIcon
                        isSelected={isSelected}
                        isHovered={isHovered}
                        isFocused={isFocused}
                        isActive={isActive}
                      >
                        <ChevronRightIcon width="6" height="10" />
                      </ChevronIcon>
                    </LocationCard>
                  );
                })
              )}
            </LocationList>
          </LocationListWrapper>
        </Content>
        <BottomFadeOverlay isVisible={showBottomFade} position="bottom" />
      </ContentWrapper>
    </Container>
  );
};
